export type ParsedNetworkError = Readonly<{
  statusCode?: number;
  isTimeout: boolean;
  isHtmlResponse: boolean;
  errorExplanation: string;
}>;

/**
 * Parses a network error and extracts useful metadata such as:
 * - HTTP status code (if available)
 * - Whether the error was caused by a timeout
 * - Whether the server returned HTML instead of JSON
 * - A user-friendly error message
 *
 * @param {any} error - The error object received in Apollo's `onError` handler
 * @returns {number|undefined} return.statusCode - The HTTP status code (if available)
 * @returns {boolean} return.isTimeout - Whether the error was caused by a timeout
 * @returns {boolean} return.isHtmlResponse - Whether the response was an HTML error page
 * @returns {string} return.errorExplanation - A more user-friendly error message
 */
export const parseNetworkErrorDetails = (error: unknown): ParsedNetworkError => {
  const isObject = (val: unknown): val is Record<string, unknown> => typeof val === 'object' && val !== null;

  const safeError: Readonly<Record<string, unknown>> = isObject(error) ? (error as Readonly<Record<string, unknown>>) : {};

  const networkError: Readonly<Record<string, unknown>> = isObject(safeError.networkError) ? (safeError.networkError as Readonly<Record<string, unknown>>) : {};

  const response: Readonly<Record<string, unknown>> = isObject(networkError.response) ? (networkError.response as Readonly<Record<string, unknown>>) : {};

  const rawMessage = typeof safeError.message === 'string' ? safeError.message : '';
  const message = rawMessage.toLowerCase();

  // Safely get content-type
  const headers = response?.headers;
  const contentType = headers instanceof Headers ? headers.get('content-type') : '';

  // Detect if response is HTML
  const isHtmlByContentType = typeof contentType === 'string' && contentType.includes('text/html');
  const isHtmlByMessage = rawMessage.startsWith('Unexpected token <') || rawMessage.includes('<!DOCTYPE html');

  const isHtmlResponse = isHtmlByContentType || isHtmlByMessage;

  const statusCode = typeof networkError.statusCode === 'number' ? networkError.statusCode : typeof response.status === 'number' ? response.status : undefined;
  const isTimeout = message.includes('timed out') || message.includes('timeout') || statusCode === 408 || statusCode === 504;

  let errorExplanation = rawMessage;
  if (isTimeout) {
    errorExplanation = statusCode ? `Request timed out (status ${statusCode})` : 'Request timed out';
  } else if (isHtmlResponse) {
    errorExplanation = statusCode ? `Non-JSON response received (status ${statusCode})` : 'Non-JSON response received';
  }

  return { statusCode, isTimeout, isHtmlResponse, errorExplanation };
};
